import * as React from 'react';
import { IgcSplitterComponent as Component } from 'igniteui-webcomponents';
import type { IgcSplitterComponentEventMap } from 'igniteui-webcomponents';
import { type EventName, createComponent } from '../react-props.js';

/**
 * The `igc-splitter` component provides a resizable split-pane layout that divides the view
 * into two panels — *start* and *end* — separated by a draggable bar.
 *
 * Panels can be resized by dragging the bar, using keyboard shortcuts, or collapsed/expanded
 * using the built-in collapse buttons or the programmatic `toggle()` API.
 * Nested splitters are supported for more complex layouts.
 * @slot start - Content projected into the start (left/top) panel.
 * @slot end - Content projected into the end (right/bottom) panel.
 * @fires igcResizeStart - Emitted once when a resize operation begins (pointer drag or keyboard).
 * @fires igcResizing - Emitted continuously while a pane is being resized.
 * @fires igcResizeEnd - Emitted once when a resize operation completes.
 * @csspart splitter-bar - The resizable bar element between the two panels.
 * @csspart drag-handle - The drag handle icon/element on the splitter bar.
 * @csspart start-pane - The container for the start panel content.
 * @csspart end-pane - The container for the end panel content.
 * @csspart start-collapse-btn - The button to collapse the start panel.
 * @csspart end-collapse-btn - The button to collapse the end panel.
 * @csspart start-expand-btn - The button to expand the start panel when collapsed.
 * @csspart end-expand-btn - The button to expand the end panel when collapsed.
 * @class
 */
export const IgrSplitter = /* @__PURE__ */ createComponent({
  tagName: 'igc-splitter',
  displayName: 'IgrSplitter',
  elementClass: Component,
  react: React,
  events: {
    onResizeStart: 'igcResizeStart' as EventName<
      IgcSplitterComponentEventMap['igcResizeStart']
    >,
    onResizing: 'igcResizing' as EventName<
      IgcSplitterComponentEventMap['igcResizing']
    >,
    onResizeEnd: 'igcResizeEnd' as EventName<
      IgcSplitterComponentEventMap['igcResizeEnd']
    >,
  },
});

export type IgrSplitter = Component;

/** @deprecated Module register is no longer needed and can be removed */
export const IgrSplitterModule = Component;
